import { Entity, FieldType, PersistenceType } from 'gossamer-unified';
import { Institution } from './Institution';
import { InstitutionGroup } from './InstitutionGroup';
import { Location } from './Location';

export interface LocationTrackingScheduleRow {
  id: number;
  institution_id: number;
  institution_group_id: number;
  location_id: number;
  start_date: string;
  end_date: string;
  start_time: string;
  end_time: string;
  monday: boolean;
  tuesday: boolean;
  wednesday: boolean;
  thursday: boolean;
  friday: boolean;
  saturday: boolean;
  sunday: boolean;
  created_at: string;
  updated_at: string;
}

export const LocationTrackingSchedule: Entity<LocationTrackingScheduleRow> = {
  id: 'LocationTrackingSchedule',
  title: 'Location Tracking Schedule',
  service: 'main',
  rowLabel: (row: LocationTrackingScheduleRow) =>
    `${new Date(row.start_date).toLocaleDateString()} to ${new Date(row.end_date).toLocaleDateString()} (${
      row.start_time
    }-${row.end_time})`,
  primaryKeyFields: ['id'],
  source: {
    tableName: 'public.location_tracking_schedule',
  },
  security: [{ role: 'staff', action: 'all', access: 'all' }],
  defaultSort: [
    ['start_date', 'DESC'],
    ['start_time', 'ASC'],
  ],
  defaultFilters: [],
  listRowLink: '/admin/location-tracking-schedule/display/{key}',
  fields: {
    id: {
      seq: 5,
      type: FieldType.Integer,
      label: 'ID',
      persistence: PersistenceType.BackEndManaged,
      mandatory: true,
      listColumn: true,
      autoIncrement: true,
      fieldGroup: 'main',
    },
    institution_id: {
      seq: 10,
      type: FieldType.Reference,
      label: 'Institution',
      refEntity: Institution,
      mandatory: true,
      listColumn: true,
      persistence: PersistenceType.SetOnCreateOnly,
      fieldGroup: 'main',
    },
    institution_group_id: {
      seq: 15,
      type: FieldType.Reference,
      label: 'Group',
      refEntity: InstitutionGroup,
      mandatory: true,
      listColumn: true,
      description: 'Group whose members will be tracked',
      fieldGroup: 'main',
    },
    location_id: {
      seq: 20,
      type: FieldType.Reference,
      label: 'Location',
      refEntity: Location,
      mandatory: true,
      listColumn: true,
      description: 'Location where tracking occurs (50m radius)',
      fieldGroup: 'main',
    },
    start_date: {
      seq: 25,
      type: FieldType.Date,
      label: 'Start Date',
      mandatory: true,
      listColumn: true,
      description: 'First date this schedule is active',
      fieldGroup: 'main',
    },
    end_date: {
      seq: 30,
      type: FieldType.Date,
      label: 'End Date',
      mandatory: true,
      listColumn: true,
      description: 'Last date this schedule is active',
      fieldGroup: 'main',
    },
    start_time: {
      seq: 35,
      type: FieldType.Text,
      label: 'Start Time',
      mandatory: true,
      listColumn: true,
      description: 'Time of day tracking begins (e.g., 09:00:00)',
      regex: '\\d{2}:\\d{2}:\\d{2}',
      fieldGroup: 'main',
    },
    end_time: {
      seq: 40,
      type: FieldType.Text,
      label: 'End Time',
      mandatory: true,
      listColumn: true,
      description: 'Time of day tracking ends (e.g., 17:00:00)',
      regex: '\\d{2}:\\d{2}:\\d{2}',
      fieldGroup: 'main',
    },
    monday: {
      seq: 45,
      type: FieldType.Boolean,
      label: 'Monday',
      description: 'Track on Mondays',
      fieldGroup: 'days',
    },
    tuesday: {
      seq: 50,
      type: FieldType.Boolean,
      label: 'Tuesday',
      description: 'Track on Tuesdays',
      fieldGroup: 'days',
    },
    wednesday: {
      seq: 55,
      type: FieldType.Boolean,
      label: 'Wednesday',
      description: 'Track on Wednesdays',
      fieldGroup: 'days',
    },
    thursday: {
      seq: 60,
      type: FieldType.Boolean,
      label: 'Thursday',
      description: 'Track on Thursdays',
      fieldGroup: 'days',
    },
    friday: {
      seq: 65,
      type: FieldType.Boolean,
      label: 'Friday',
      description: 'Track on Fridays',
      fieldGroup: 'days',
    },
    saturday: {
      seq: 70,
      type: FieldType.Boolean,
      label: 'Saturday',
      description: 'Track on Saturdays',
      fieldGroup: 'days',
    },
    sunday: {
      seq: 75,
      type: FieldType.Boolean,
      label: 'Sunday',
      description: 'Track on Sundays',
      fieldGroup: 'days',
    },
    created_at: {
      seq: 80,
      type: FieldType.Datetime,
      label: 'Created At',
      persistence: PersistenceType.BackEndManaged,
      fieldGroup: 'main',
    },
    updated_at: {
      seq: 85,
      type: FieldType.Datetime,
      label: 'Updated At',
      persistence: PersistenceType.BackEndManaged,
      fieldGroup: 'main',
    },
  },
};
